// Services
import addressFinderAutocompleteService from '@/app/services/address-finder/address-finder-autocomplete/address-finder-autocomplete-service.js';

// Utils
import { vuelidateUtil } from '@/utils/vuelidate-util.js';

// Constants
import { GA } from '@/app/constants/ga-constants';
import { STATUS_CODES } from '@/app/constants/status-constants';

// Mixins
import { GoogleAnalyticsMixins } from '@/app/mixins/utils/google-analytics-mixins';
import { dateMixins } from '@/app/mixins/utils/date-mixins';
import { toastMessageMixins } from '@/app/mixins/utils/toast-message-mixins';
import { imageCompressorMixins } from '@/app/mixins/utils/image-compressor-mixins';
import { inputTextMixins } from '@/app/mixins/utils/input-text-mixins';

// Components
const FileUpload = () => import(/* webpackPrefetch: true, webpackChunkName: "file-upload" */ '@/app/views/main-view/_shared/_components/file-upload/file-upload.vue');

export default {
	name: 'AddHuman',
	components: { FileUpload },
	mixins: [
		GoogleAnalyticsMixins,
		dateMixins,
		toastMessageMixins,
		imageCompressorMixins,
		inputTextMixins
	],
	data() {
		return {
			inputFields: {
				firstName: '',
				lastName: '',
				email: '',
				selectedTimeZone: 'Australia/Sydney',
				coverage: '',
				bio: '',
				address: '',
				profileImageUpload: null
			},
			isAgreeTNC: false,
			isAPILoading: false,
			imageToCrop: '',
			addressList: [],
			isAddressLoading: false
		}
	},
	computed: {
		validationRules() {
			let fields = {
				inputFields: {
					firstName: {},
					lastName: {},
					email: {},
					address: {},
					coverage: {},
					bio: {},
					profileImageUpload: {}
				}
			};

			fields.inputFields = vuelidateUtil.setFieldsRequired(fields.inputFields, true);
			fields.inputFields = vuelidateUtil.setEmailValidation(fields.inputFields, 'email');
			return fields;
		},
		isDisabled() {
			return this.$v.$invalid || this.isAPILoading;
		},
		authStatus() {
			return this.$store.getters.getAuthMessage;
		},
		hasUploaded() {
			let img = '';
			
			if (this.profileImageSrc) {
				img = this.profileImageSrc;
			} else {
				img = require('@/assets/images/_shared/default-profile-pic.png')
			}

			return img;
		},
	},
	watch: {
		'inputFields.coverage': {
			immediate: true,
			handler(newVal) {
				if (newVal) {
					if ( newVal <= 100 ) {
						this.inputFields.coverage = newVal;
					} else {
						this.inputFields.coverage = newVal.slice(0, -1);
					}
				}
			},
			deep: true
		}  
	},
	methods: {
		setActiveComponent() {
			this.$emit('setActiveComponent', 'add-pet');
		},
		sendSocialMediaRegistrationAnalytics(isFB) {
			let action = isFB ? GA.REGISTRATION_PAGE.CREATE_ACCOUNT_WITH_FB_BUTTON.ACTION : GA.REGISTRATION_PAGE.CREATE_ACCOUNT_WITH_GOOGLE_BUTTON.ACTION;
			let label = isFB ? GA.REGISTRATION_PAGE.CREATE_ACCOUNT_WITH_FB_BUTTON.LABEL : GA.REGISTRATION_PAGE.CREATE_ACCOUNT_WITH_GOOGLE_BUTTON.LABEL;

			this.sendEventTrackingToGA(action, GA.REGISTRATION_PAGE.CATEGORY, label);
		},
		async onAddHuman() {
			if (this.isDisabled) {
				return;
			}

			const {
				firstName,
				lastName,
				email,
				selectedTimeZone,
				coverage,
				bio,
				address
			} = this.inputFields;

			const userDetail = {
				firstName,
				lastName,
				email,
				selectedTimeZone,
				coverage,
				bio,
				address: address.displayAddress
			}

			await this.getAddressMetaData(this.inputFields.address);
			this.$emit('setActiveComponent', 'select-preferred-breeds');
			this.$store.commit('setRegistrationUserDetails', { ...this.userDetails, userDetail });
		},
		async onUploadImage(event) {
			let valid = await this.validateImage(event.target.files[0]);
			// do nothing when user cancels Upload
			if(!valid) {
				// reset uploader
				this.$refs.imgUploader.value = '';
				return;
			}

			// compress image if more than 2mb
			const file = await this.compressImage(event.target.files[0]);

			this.userDetails.profileImage = [file];
			// show preview of pet image 
			let reader = new FileReader();

			reader.onload = () => {
				this.imageToCrop = reader.result;
				this.$refs.modalFileUpload.show();
			}

			this.sendEventTrackingToGA(GA.REGISTRATION_PAGE.ADD_PET_IMAGE.ACTION, GA.REGISTRATION_PAGE.CATEGORY, GA.REGISTRATION_PAGE.ADD_PET_IMAGE.LABEL);
			reader.readAsDataURL(file); 
		},
		clearFileInput(event) {
			event.target.value = '';
		},
		findAddress(address) {
			if (address) {
				this.isAddressLoading = true;

				this.delayTyping(() => {
					this.getAutocompleteAddress(address);
				});
			}
		},
		onBlur(field) {
			if (field === 'address') {
				this.$v.inputFields.address.$touch();
			}
		},
		getAutocompleteAddress(address) {
			addressFinderAutocompleteService.getAutocompleteAddress(address)
				.then(res => {
					if (res.status === STATUS_CODES.SUCCESS) {
						this.addressList = res.data.completions.map(add => { 
							let tempAddress = add.full_address.split(" ");
							add.displayAddress = `${tempAddress[tempAddress.length - 2]} ${tempAddress[tempAddress.length - 1]}`;
							add.label = address.id;
							return address; 
						});
						this.isAddressLoading = false;
					} else {
						const { ERROR_GET_ADDRESS } = this.toastMessages.addressFinder();
						this.showAPIErrorToast(ERROR_GET_ADDRESS, res);
						this.isAddressLoading = false;
					}
				})
				.catch(() => {
					this.showGenericErrorToast();
					this.isAddressLoading = false;
				});
		},
		async getAddressMetaData(address) {
			addressFinderAutocompleteService.getAddressMetaData(address.id)
				.then(res => {
					if (res.status === STATUS_CODES.SUCCESS) {
						const { latitude, longitude } = res.data;
						this.$store.commit('setGeoLocation', { latitude, longitude });
					} else {
						const { ERROR_GET_ADDRESS } = this.toastMessages.addressFinder();
						this.showAPIErrorToast(ERROR_GET_ADDRESS, res);
						this.isAddressLoading = false;
					}
				})
				.catch(() => {
					this.showGenericErrorToast();
					this.isAddressLoading = false;
				});
		},
		setImageCrop({image, blob}) {
			this.$store.commit('setProfileImageSrc', image);
			this.userDetails.profileImageUpload = blob;
			this.inputFields.profileImageUpload = blob;
		},
		checkCoverageValue() {
			if ( this.inputFields.coverage < 5 ) {
				this.inputFields.coverage = 5;
			} else {
				this.$v.inputFields.coverage.$touch();
			}	
		}
	},
	validations() {
		return this.validationRules;
	}
}